import { Link, useMatchRoute } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { useFilterStore } from '@/stores/filters'
import {
  LayoutDashboard,
  Users,
  Package,
  ShoppingCart,
  CreditCard,
  Shield,
  HardDrive,
  Brain,
  Plug,
  Settings,
  ChevronLeft,
  FileText,
  Receipt,
  Zap,
  Activity,
  History,
  RefreshCw,
  CalendarClock,
} from 'lucide-react'

interface NavItem {
  label: string
  to: string
  icon: React.ElementType
}

interface NavSection {
  title: string
  items: NavItem[]
}

const navigation: NavSection[] = [
  {
    title: 'Overview',
    items: [
      { label: 'Dashboard', to: '/', icon: LayoutDashboard },
    ],
  },
  {
    title: 'Customers',
    items: [
      { label: 'Tenants', to: '/tenants', icon: Users },
      { label: 'Subscriptions', to: '/subscriptions', icon: FileText },
    ],
  },
  {
    title: 'Commerce',
    items: [
      { label: 'Catalog', to: '/catalog', icon: Package },
      { label: 'Orders', to: '/orders', icon: ShoppingCart },
    ],
  },
  {
    title: 'Billing',
    items: [
      { label: 'Invoices', to: '/billing/invoices', icon: Receipt },
      { label: 'Payments', to: '/billing/payments', icon: CreditCard },
    ],
  },
  {
    title: 'Operations',
    items: [
      { label: 'Security', to: '/security', icon: Shield },
      { label: 'Backups', to: '/backups', icon: HardDrive },
      { label: 'Activity', to: '/activity', icon: History },
      { label: 'Reconciliation', to: '/reconciliation', icon: RefreshCw },
      { label: 'Renewals', to: '/renewals', icon: CalendarClock },
    ],
  },
  {
    title: 'CloudMania',
    items: [
      { label: 'Customers', to: '/appxite/customers', icon: Users },
      { label: 'Orders', to: '/appxite/orders', icon: ShoppingCart },
      { label: 'Subscriptions', to: '/appxite/subscriptions', icon: FileText },
    ],
  },
  {
    title: 'AOS',
    items: [
      { label: 'Overview', to: '/operations', icon: Brain },
      { label: 'Triggers', to: '/operations/triggers', icon: Zap },
      { label: 'Events', to: '/operations/events', icon: Activity },
    ],
  },
  {
    title: 'System',
    items: [
      { label: 'Integrations', to: '/integrations', icon: Plug },
      { label: 'Settings', to: '/settings', icon: Settings },
    ],
  },
]

function SidebarHeader({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  return (
    <div className="flex h-14 items-center border-b px-4">
      {!collapsed && (
        <span className="text-lg font-bold tracking-tight">Mzanzi</span>
      )}
      <button
        onClick={onToggle}
        className={cn(
          'ml-auto rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          collapsed && 'mx-auto'
        )}
      >
        <ChevronLeft
          className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')}
        />
      </button>
    </div>
  )
}

function NavSectionGroup({ section, collapsed, matchRoute }: {
  section: NavSection; collapsed: boolean; matchRoute: ReturnType<typeof useMatchRoute>
}) {
  return (
    <div className="mb-2">
      {!collapsed && (
        <p className="mb-1 px-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {section.title}
        </p>
      )}
      {section.items.map((item) => {
        const isActive = item.to === '/'
          ? matchRoute({ to: '/' })
          : matchRoute({ to: item.to, fuzzy: true })
        return (
          <Link
            key={item.to}
            to={item.to}
            className={cn(
              'flex items-center gap-3 px-4 py-2 text-sm font-medium transition-colors',
              'hover:bg-accent hover:text-accent-foreground',
              isActive
                ? 'bg-accent text-accent-foreground'
                : 'text-muted-foreground',
              collapsed && 'justify-center px-2'
            )}
            title={collapsed ? item.label : undefined}
          >
            <item.icon className="h-4 w-4 shrink-0" />
            {!collapsed && <span>{item.label}</span>}
          </Link>
        )
      })}
    </div>
  )
}

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar } = useFilterStore()
  const matchRoute = useMatchRoute()

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-30 flex flex-col border-r bg-card transition-all duration-200',
        sidebarCollapsed ? 'w-16' : 'w-60'
      )}
    >
      <SidebarHeader collapsed={sidebarCollapsed} onToggle={toggleSidebar} />

      <nav className="flex-1 overflow-y-auto py-2">
        {navigation.map((section) => (
          <NavSectionGroup key={section.title} section={section} collapsed={sidebarCollapsed} matchRoute={matchRoute} />
        ))}
      </nav>

      {!sidebarCollapsed && (
        <div className="border-t p-4">
          <p className="text-xs text-muted-foreground">Mzanzi Admin v0.1.0</p>
        </div>
      )}
    </aside>
  )
}
